/**
 * Collection filter + pagination via Shopify Section Rendering API.
 *
 * When a filter form is submitted OR a pagination link is clicked, fetch
 * `?section_id={id}&{params}` instead of loading the full page, then swap
 * only the grid contents. URL is updated via pushState so back/forward,
 * copy/paste, and share still work.
 *
 * Progressive enhancement: if JS fails to load or errors, the form + links
 * fall back to their default full-page-reload behavior (form action is the
 * current URL, pagination anchors have real hrefs).
 *
 * Hook points (in the DOM):
 *   [data-collection-grid]            — the swap target (its inner HTML is replaced)
 *   [data-collection-filter-form]     — filter form; submit is intercepted
 *   [data-collection-grid] .pagination a — pagination anchors; click intercepted
 */
(function () {
  'use strict';

  const gridEl = document.querySelector('[data-collection-grid]');
  if (!gridEl) return;

  // Section Rendering API expects the section's TYPE (file name), not the
  // JSON template key. In collection.json the key is "main" but the type
  // is "main-collection" — passing "main" 404s. Read from data attribute.
  const sectionId = gridEl.dataset.sectionType;
  if (!sectionId) return;

  const filterForm = document.querySelector('[data-collection-filter-form]');

  /**
   * Fetch the collection section HTML for a given URL search string
   * and replace the grid contents. Updates history via pushState.
   */
  async function updateGrid(newSearch, options = {}) {
    const { pushHistory = true, scrollToTop = true } = options;

    // Show loading state
    gridEl.setAttribute('aria-busy', 'true');
    gridEl.style.opacity = '0.5';

    // Section Rendering API expects section_id query param
    const params = new URLSearchParams(newSearch);
    params.set('section_id', sectionId);

    try {
      const response = await fetch(`${window.location.pathname}?${params.toString()}`, {
        headers: { 'Accept': 'text/html' },
      });
      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
      const html = await response.text();

      // Parse response and extract the fresh grid
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-collection-grid]');
      if (!newGrid) throw new Error('No [data-collection-grid] in response');

      // Swap inner HTML — keeps outer wrapper attributes stable
      gridEl.innerHTML = newGrid.innerHTML;

      // Update URL (strip section_id — user-facing URL shouldn't leak it)
      if (pushHistory) {
        const userParams = new URLSearchParams(newSearch);
        userParams.delete('section_id');
        const qs = userParams.toString();
        const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
        window.history.pushState({ collectionFilter: true }, '', newUrl);
      }

      if (scrollToTop) {
        gridEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (err) {
      // Fall back to full-page reload — safer than showing broken state
      console.error('[collection-filter]', err);
      const fallbackUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
      window.location.href = fallbackUrl;
    } finally {
      gridEl.removeAttribute('aria-busy');
      gridEl.style.opacity = '';
    }
  }

  /**
   * Extract URL search string from a filter form's checked inputs.
   * Preserves the existing custom `vf` param via a hidden input if present.
   */
  function formToSearchString(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value !== '' && value !== null) params.append(key, value);
    }
    // Reset to page 1 whenever filters change
    params.delete('page');
    return params.toString();
  }

  // Filter form submit
  if (filterForm) {
    // Auto-submit on any input change (checkbox toggle, price input blur)
    filterForm.addEventListener('change', (event) => {
      if (event.target.matches('input, select')) {
        updateGrid(formToSearchString(filterForm));
      }
    });

    // Prevent the noscript submit button from ever full-page-reloading when JS is on
    filterForm.addEventListener('submit', (event) => {
      event.preventDefault();
      updateGrid(formToSearchString(filterForm));
    });
  }

  // Pagination click delegation — captures clicks on <a> inside pagination nav
  gridEl.addEventListener('click', (event) => {
    const link = event.target.closest('.pagination a[href]');
    if (!link) return;
    // Let modifier-clicks fall through (cmd/ctrl-click to new tab etc.)
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const url = new URL(link.href, window.location.origin);
    updateGrid(url.search.replace(/^\?/, ''));
  });

  // Back/forward browser buttons
  window.addEventListener('popstate', () => {
    updateGrid(window.location.search.replace(/^\?/, ''), {
      pushHistory: false,
      scrollToTop: false,
    });
  });
})();
